<script>
    import { onMount, onDestroy } from 'svelte';
    import { selectedConversation, currentUser } from './store.js';
    import { conversationsAPI, chatbotAPI, labelsAPI, crmAPI } from './api.js';
    import { on as rtOn, joinConversation, leaveConversation } from './lib/realtime.js';
    import ConversationCollabBar from './ConversationCollabBar.svelte';
    import Icon from './lib/Icon.svelte';

    // Append a message guarding against duplicates by id (socket echo can
    // arrive before the POST resolves; webhooks can redeliver).
    function appendMessage(msg) {
        if (!msg) return;
        if (messages.some(m => m.id === msg.id)) return;
        messages = [...messages, msg];
        scrollToBottom();
    }

    function handleCollabUpdate(updated) {
        // Merge the result into the selected conversation store
        selectedConversation.update(curr => curr ? { ...curr, ...updated } : updated);
    }

    let messages = [];
    let newMessage = '';
    let sending = false;
    let messagesContainer;
    let loadedConversationId = null;
    let pollInterval = null;
    let fileInput;
    let selectedFile = null;
    let uploadingFile = false;

    // AI state
    let aiEnabled = false;
    let availableChatbots = [];
    let selectedChatbotId = null;
    let loadingAI = false;
    let showChatbotSelector = false;

    let rtUnsub = null;
    let rtStatusUnsub = null;

    // Labels (platform tags) on this conversation
    let allLabels = [];
    let showLabelPicker = false;
    async function loadAllLabels() {
        try { allLabels = await labelsAPI.getAll(); } catch (e) { allLabels = []; }
    }
    // Labels belong to the CONTACT (visible across all their conversations and
    // in the Contacts card), not to the individual conversation.
    async function addLabel(labelId) {
        const contactId = $selectedConversation?.contact?.id;
        if (!contactId) return;
        try {
            const updated = await labelsAPI.addToContact(contactId, labelId);
            selectedConversation.update(c => c && c.contact
                ? { ...c, contact: { ...c.contact, labels: updated.labels } } : c);
            showLabelPicker = false;
        } catch (e) { console.error('addLabel', e); }
    }
    async function removeLabel(labelId) {
        const contactId = $selectedConversation?.contact?.id;
        if (!contactId) return;
        try {
            const updated = await labelsAPI.removeFromContact(contactId, labelId);
            selectedConversation.update(c => c && c.contact
                ? { ...c, contact: { ...c.contact, labels: updated.labels } } : c);
        } catch (e) { console.error('removeLabel', e); }
    }

    // ----- Lead / CRM panel (drawer) -----
    // Backend already exposes everything we need (see crmAPI): this is UI only.
    let lead = null;            // the lead tied to this conversation, if any
    let pipelines = [];         // cached pipelines (with stages) for the stage <select>
    let leadLoading = false;
    let leadBusy = false;       // an action (create/move/won/lost) is in flight
    let showLeadPanel = false;

    // The stages belonging to the lead's own pipeline (moving to a stage from
    // another pipeline is rejected by the backend with 400).
    $: leadStages = (lead && pipelines.find(p => p.id === lead.pipeline_id)?.stages) || [];

    async function loadLead() {
        const contactId = $selectedConversation?.contact?.id;
        if (!contactId) { lead = null; return; }
        leadLoading = true;
        try {
            // Cache pipelines once; they rarely change during a session.
            if (!pipelines.length) {
                try { pipelines = await crmAPI.getPipelines(); } catch (e) { pipelines = []; }
            }
            const leads = await crmAPI.listLeads(null, null, contactId);
            const list = Array.isArray(leads) ? leads : [];
            // Prefer the lead created from THIS conversation; else the first one.
            lead = list.find(l => l.custom_fields?.conversation_id === $selectedConversation.id)
                   || list[0] || null;
        } catch (e) {
            console.error('loadLead', e);
            lead = null;
        } finally {
            leadLoading = false;
        }
    }

    async function createLead() {
        if (!$selectedConversation || leadBusy) return;
        leadBusy = true;
        try {
            lead = await crmAPI.createLeadFromConversation($selectedConversation.id);
            if (!pipelines.length) {
                try { pipelines = await crmAPI.getPipelines(); } catch (e) { pipelines = []; }
            }
        } catch (e) {
            console.error('createLead', e);
            alert('No se pudo crear el lead.');
        } finally { leadBusy = false; }
    }

    async function moveLeadStage(e) {
        const stageId = parseInt(e.target.value, 10);
        if (!lead || !stageId || stageId === lead.stage_id || leadBusy) return;
        leadBusy = true;
        try {
            lead = await crmAPI.moveLead(lead.id, stageId);
        } catch (err) {
            console.error('moveLeadStage', err);
            e.target.value = lead.stage_id; // revert select on failure
        } finally { leadBusy = false; }
    }

    async function markLead(status) {
        if (!lead || leadBusy) return;
        leadBusy = true;
        try {
            lead = await crmAPI.updateLead(lead.id, { status });
        } catch (err) {
            console.error('markLead', err);
            alert('No se pudo actualizar el lead.');
        } finally { leadBusy = false; }
    }

    $: if ($selectedConversation && $selectedConversation.id !== loadedConversationId) {
        if (loadedConversationId) leaveConversation(loadedConversationId);
        loadConversation();
        joinConversation($selectedConversation.id);
        startPolling();
        // Reset and reload the lead panel for the new conversation
        lead = null;
        showLeadPanel = false;
        loadLead();
    }

    onMount(() => {
        loadChatbots();
        loadAllLabels();
        // Real-time message stream (room joined per conversation)
        rtUnsub = rtOn('new_message', (payload) => {
            if (!$selectedConversation || payload.conversation_id !== $selectedConversation.id) return;
            appendMessage(payload.message);
        });
        // Delivery status for outgoing messages (sent/failed) — updates in place
        // so the agent sees real state instead of a stuck 'pending'.
        rtStatusUnsub = rtOn('message_status', (payload) => {
            if (!$selectedConversation || payload.conversation_id !== $selectedConversation.id) return;
            messages = messages.map(m =>
                m.id === payload.message_id
                    ? { ...m, delivery_status: payload.status, failed_reason: payload.reason }
                    : m
            );
        });
    });

    onDestroy(() => {
        stopPolling();
        if (rtUnsub) rtUnsub();
        if (rtStatusUnsub) rtStatusUnsub();
        if (loadedConversationId) leaveConversation(loadedConversationId);
    });

    function startPolling() {
        stopPolling();
        // Slow fallback only — sockets deliver messages in real time
        pollInterval = setInterval(refreshMessages, 30000);
    }

    function stopPolling() {
        if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
        }
    }

    async function refreshMessages() {
        if (!$selectedConversation) return;
        try {
            const conv = await conversationsAPI.getById($selectedConversation.id);
            if (!conv.messages) return;
            // Merge by id rather than replacing wholesale, so an optimistic or
            // socket-delivered message isn't briefly duplicated.
            const known = new Set(messages.map(m => m.id));
            const merged = conv.messages.filter(m => !known.has(m.id));
            if (merged.length) {
                messages = [...messages, ...merged];
                scrollToBottom();
            }
        } catch (error) {
            // Silently fail on refresh
        }
    }

    async function loadChatbots() {
        try {
            const chatbots = await chatbotAPI.getAll();
            availableChatbots = chatbots.filter(c => c.is_active);
        } catch (error) {
            console.error('Error loading chatbots:', error);
        }
    }

    async function loadConversation() {
        if (!$selectedConversation) return;

        const convId = $selectedConversation.id;
        loadedConversationId = convId;

        try {
            const conv = await conversationsAPI.getById(convId);
            messages = conv.messages || [];
            // Merge the fresh server copy into the store so labels, contact and
            // status stay in sync (the list view may carry a stale snapshot).
            selectedConversation.update(c =>
                c && c.id === conv.id ? { ...c, ...conv, messages: undefined } : c);
            await loadAIStatus();
            scrollToBottom();
            // Clear the unread badge (best-effort; the list updates via socket)
            conversationsAPI.markRead(convId).catch(() => {});
        } catch (error) {
            console.error('Error loading conversation:', error);
        }
    }

    async function loadAIStatus() {
        if (!$selectedConversation) return;

        try {
            const status = await chatbotAPI.getConversationStatus($selectedConversation.id);
            aiEnabled = status.enabled || false;
            if (status.chatbot_config) {
                selectedChatbotId = status.chatbot_config.id;
            }
        } catch (error) {
            console.error('Error loading AI status:', error);
            aiEnabled = false;
        }
    }

    async function toggleAI() {
        if (!$selectedConversation) return;

        if (!aiEnabled && !selectedChatbotId) {
            showChatbotSelector = true;
            return;
        }

        loadingAI = true;
        try {
            if (aiEnabled) {
                // Disable AI
                await chatbotAPI.disableForConversation($selectedConversation.id);
                aiEnabled = false;
            } else {
                // Enable AI
                await chatbotAPI.enableForConversation($selectedConversation.id, selectedChatbotId);
                aiEnabled = true;
                showChatbotSelector = false;
                await loadConversation(); // Reload to get greeting message
            }
        } catch (error) {
            console.error('Error toggling AI:', error);
            alert('Error al activar/desactivar AI');
        } finally {
            loadingAI = false;
        }
    }

    function selectChatbotAndEnable(chatbotId) {
        selectedChatbotId = chatbotId;
        toggleAI();
    }

    async function sendMessage() {
        if ((!newMessage.trim() && !selectedFile) || sending) return;

        const convId = $selectedConversation.id;
        const fileToSend = selectedFile;
        sending = true;
        try {
            let attachments = [];

            // Upload file if selected (must finish before we can send it)
            if (fileToSend) {
                uploadingFile = true;
                const uploaded = await uploadFile(fileToSend);
                if (uploaded) {
                    attachments.push(uploaded);
                }
                uploadingFile = false;
            }

            const text = newMessage.trim() || (attachments.length ? '[Archivo adjunto]' : '');

            // Clear the composer and unblock immediately. Delivery to the external
            // channel (WhatsApp/Baileys) can be slow; the sent message arrives via the
            // 'new_message' socket event, so we don't block the UI waiting for the POST.
            newMessage = '';
            selectedFile = null;
            sending = false;
            uploadingFile = false;

            conversationsAPI.sendMessage(convId, text, attachments)
                .then((message) => {
                    // Fallback for environments without realtime: append if not present
                    if (message && message.id && !messages.some(m => m.id === message.id)) {
                        messages = [...messages, message];
                        scrollToBottom();
                    }
                })
                .catch((error) => {
                    console.error('Error sending message:', error);
                    alert('Failed to send message');
                });
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Failed to send message');
            sending = false;
            uploadingFile = false;
        }
    }

    async function uploadFile(file) {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch('/api/upload', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (!response.ok) throw new Error('Upload failed');
            return await response.json();
        } catch (error) {
            console.error('Error uploading file:', error);
            return null;
        }
    }

    function handleFileSelect(event) {
        const file = event.target.files[0];
        if (file) {
            selectedFile = file;
        }
    }

    function removeSelectedFile() {
        selectedFile = null;
        if (fileInput) fileInput.value = '';
    }

    function scrollToBottom() {
        setTimeout(() => {
            if (messagesContainer) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }
        }, 100);
    }

    function formatMessageTime(dateString) {
        const date = new Date(dateString);
        return date.toLocaleTimeString('es-CR', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Costa_Rica'
        });
    }

    // Direction is decided by message_type, NOT by user_id: bot/flow/AI
    // messages are outgoing with no user_id and must still render as ours.
    function isOutgoing(message) {
        return message.message_type === 'outgoing';
    }
    function isInternalNote(message) {
        return message.message_type === 'private_note' || message.message_type === 'activity';
    }
    function senderLabel(message) {
        if (message.message_type === 'incoming') {
            return $selectedConversation?.contact?.name || 'Cliente';
        }
        // Outgoing: a human agent has a sender; otherwise it's the bot/IA
        return message.sender?.name || 'Bot';
    }

    async function changeStatus(e) {
        const status = e.target.value;
        if (!$selectedConversation || status === $selectedConversation.status) return;
        try {
            const updated = await conversationsAPI.update($selectedConversation.id, { status });
            selectedConversation.set({ ...$selectedConversation, ...updated });
        } catch (err) {
            console.error('Error updating status:', err);
            e.target.value = $selectedConversation.status; // revert
        }
    }

</script>

<div class="conversation-detail">
    {#if $selectedConversation}
        <!-- Header -->
        <div class="detail-header">
            <div class="contact-info">
                <button
                    class="back-btn"
                    aria-label="Volver a la bandeja"
                    on:click={() => selectedConversation.set(null)}
                >
                    <Icon name="chevron-left" size={22} />
                </button>
                <div class="contact-avatar">
                    {$selectedConversation.contact?.name?.charAt(0) || '?'}
                </div>
                <div>
                    <h3>{$selectedConversation.contact?.name || 'Unknown'}</h3>
                    <p class="contact-email">{$selectedConversation.contact?.email || ''}</p>
                    {#if aiEnabled}
                        <p class="ai-status">🤖 AI activo</p>
                    {/if}
                    <div class="conv-labels">
                        {#each ($selectedConversation.contact?.labels || []) as lb (lb.id)}
                            <span class="conv-label-chip" style="background:{lb.color}">
                                {lb.name}
                                <button class="clc-x" on:click={() => removeLabel(lb.id)} title="Quitar" aria-label="Quitar etiqueta">×</button>
                            </span>
                        {/each}
                        <div class="label-add-wrap">
                            <button class="label-add" on:click={() => showLabelPicker = !showLabelPicker} title="Añadir etiqueta">+ etiqueta</button>
                            {#if showLabelPicker}
                                <div class="label-menu">
                                    {#each allLabels.filter(l => !($selectedConversation.contact?.labels || []).some(x => x.id === l.id)) as l (l.id)}
                                        <button class="label-opt" on:click={() => addLabel(l.id)}>
                                            <span class="lo-dot" style="background:{l.color}"></span>{l.name}
                                        </button>
                                    {:else}
                                        <span class="label-none">No hay más etiquetas</span>
                                    {/each}
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
            </div>
            <div class="conversation-actions">
                <button
                    class="ai-toggle-button"
                    class:ai-active={aiEnabled}
                    on:click={toggleAI}
                    disabled={loadingAI}
                    title={aiEnabled ? 'Desactivar AI' : 'Activar AI'}
                >
                    {#if loadingAI}
                        ⏳
                    {:else if aiEnabled}
                        🤖 AI Activo
                    {:else}
                        🤖 Activar AI
                    {/if}
                </button>
                <select class="status-select" value={$selectedConversation.status} on:change={changeStatus}>
                    <option value="open">Abierta</option>
                    <option value="pending">Pendiente</option>
                    <option value="resolved">Resuelta</option>
                    <option value="closed">Cerrada</option>
                </select>
                <button
                    class="lead-toggle-button"
                    class:has-lead={!!lead}
                    on:click={() => showLeadPanel = !showLeadPanel}
                    title="Lead / CRM"
                >
                    💼 {lead ? 'Lead' : '+ Lead'}
                    {#if lead && lead.status === 'won'}<span class="lead-badge won">●</span>
                    {:else if lead && lead.status === 'lost'}<span class="lead-badge lost">●</span>{/if}
                </button>
            </div>
        </div>

        <!-- Lead / CRM drawer -->
        {#if showLeadPanel}
            <button class="lead-backdrop" aria-label="Cerrar panel de lead"
                    on:click={() => showLeadPanel = false}></button>
            <aside class="lead-drawer">
                <div class="lead-drawer-head">
                    <h4>💼 Lead</h4>
                    <button class="lead-close" on:click={() => showLeadPanel = false} aria-label="Cerrar">×</button>
                </div>

                {#if leadLoading}
                    <p class="lead-muted">Cargando…</p>
                {:else if !lead}
                    <p class="lead-muted">Esta conversación no tiene un lead asociado.</p>
                    <button class="lead-create-btn" on:click={createLead} disabled={leadBusy}>
                        {leadBusy ? 'Creando…' : '+ Crear lead desde esta conversación'}
                    </button>
                {:else}
                    <div class="lead-card">
                        <div class="lead-title">{lead.title}</div>
                        <div class="lead-meta">
                            {#if lead.value}<span class="lead-value">{lead.currency} {lead.value}</span>{/if}
                            {#if lead.status === 'won'}<span class="lead-status won">Ganado</span>
                            {:else if lead.status === 'lost'}<span class="lead-status lost">Perdido</span>
                            {:else}<span class="lead-status active">Activo</span>{/if}
                        </div>

                        <label class="lead-field">
                            <span>Etapa</span>
                            <select value={lead.stage_id} on:change={moveLeadStage} disabled={leadBusy}>
                                {#each leadStages as st (st.id)}
                                    <option value={st.id}>{st.name}</option>
                                {/each}
                            </select>
                        </label>

                        <div class="lead-actions">
                            <button class="lead-won" on:click={() => markLead('won')}
                                    disabled={leadBusy || lead.status === 'won'}>🎉 Ganado</button>
                            <button class="lead-lost" on:click={() => markLead('lost')}
                                    disabled={leadBusy || lead.status === 'lost'}>❌ Perdido</button>
                        </div>
                    </div>
                {/if}
            </aside>
        {/if}

        <!-- Collaboration bar (claim / transfer / AI status) -->
        <ConversationCollabBar
            conversation={$selectedConversation}
            onUpdated={handleCollabUpdate}
        />

        <!-- Messages -->
        <div class="messages-container" bind:this={messagesContainer}>
            {#if messages.length === 0}
                <div class="empty-messages">
                    <p>No messages yet. Start the conversation!</p>
                </div>
            {:else}
                {#each messages as message (message.id)}
                    {#if isInternalNote(message)}
                        <div class="internal-note">
                            <span class="note-tag">📝 Nota interna{message.sender?.name ? ` · ${message.sender.name}` : ''}</span>
                            <div class="note-body">{message.content}</div>
                            <span class="note-time">{formatMessageTime(message.created_at)}</span>
                        </div>
                    {:else}
                    <div class="message {isOutgoing(message) ? 'message-outgoing' : 'message-incoming'}">
                        <div class="message-bubble">
                            <div class="message-header">
                                <span class="message-sender">
                                    {senderLabel(message)}
                                </span>
                                <span class="message-time">
                                    {formatMessageTime(message.created_at)}
                                </span>
                            </div>
                            <!-- Attachments -->
                            {#if message.attachments && message.attachments.length > 0}
                                <div class="message-attachments">
                                    {#each message.attachments as attachment}
                                        {#if attachment.type === 'image'}
                                            <a href={attachment.url} target="_blank" class="attachment-image">
                                                <img src={attachment.url} alt={attachment.original_name || 'Image'} />
                                            </a>
                                        {:else if attachment.type === 'video'}
                                            <video controls class="attachment-video">
                                                <source src={attachment.url} type={attachment.mimetype || 'video/mp4'}>
                                            </video>
                                        {:else if attachment.type === 'audio'}
                                            <audio controls class="attachment-audio">
                                                <source src={attachment.url} type={attachment.mimetype || 'audio/mpeg'}>
                                            </audio>
                                        {:else}
                                            <a href={attachment.url} target="_blank" class="attachment-file">
                                                <span class="file-icon">📎</span>
                                                <span class="file-name">{attachment.original_name || 'Download'}</span>
                                            </a>
                                        {/if}
                                    {/each}
                                </div>
                            {/if}
                            <!-- Only show text if it's not a generic placeholder -->
                            {#if message.content && !message.content.match(/^\[image\]$|^\[video\]$|^\[audio\]$|^\[document\]$/i)}
                                <div class="message-content">
                                    {message.content}
                                </div>
                            {/if}
                        </div>
                    </div>
                    {/if}
                {/each}
            {/if}
        </div>

        <!-- Input -->
        <div class="message-input-container">
            {#if selectedFile}
                <div class="file-preview">
                    <span class="file-info">
                        {#if selectedFile.type.startsWith('image/')}
                            <img src={URL.createObjectURL(selectedFile)} alt="Preview" class="preview-thumb" />
                        {:else}
                            <span class="file-icon-large">📎</span>
                        {/if}
                        <span class="file-name-preview">{selectedFile.name}</span>
                    </span>
                    <button type="button" class="remove-file" on:click={removeSelectedFile}>✕</button>
                </div>
            {/if}
            <form on:submit|preventDefault={sendMessage} class="message-form">
                <input
                    type="file"
                    bind:this={fileInput}
                    on:change={handleFileSelect}
                    accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
                    style="display: none;"
                />
                <button type="button" class="attach-button" on:click={() => fileInput.click()} disabled={sending}>
                    📎
                </button>
                <input
                    type="text"
                    bind:value={newMessage}
                    placeholder="Type a message..."
                    disabled={sending}
                    class="message-input"
                />
                <button type="submit" disabled={sending || (!newMessage.trim() && !selectedFile)} class="send-button">
                    {uploadingFile ? 'Subiendo...' : sending ? 'Enviando...' : 'Enviar'}
                </button>
            </form>
        </div>
    {:else}
        <div class="no-conversation">
            <p>Select a conversation to view messages</p>
        </div>
    {/if}
</div>

<!-- Chatbot Selector Modal -->
{#if showChatbotSelector}
    <div class="modal-overlay" on:click={() => showChatbotSelector = false}>
        <div class="modal-content" on:click|stopPropagation>
            <h3>Selecciona un Chatbot</h3>
            <p class="modal-description">Elige qué chatbot activar para esta conversación</p>

            {#if availableChatbots.length === 0}
                <div class="no-chatbots">
                    <p>No hay chatbots activos disponibles.</p>
                    <p class="hint">Configura un chatbot en la sección de Chatbots.</p>
                </div>
            {:else}
                <div class="chatbot-list">
                    {#each availableChatbots as chatbot}
                        <button
                            class="chatbot-option"
                            on:click={() => selectChatbotAndEnable(chatbot.id)}
                        >
                            <div class="chatbot-option-icon">🤖</div>
                            <div class="chatbot-option-details">
                                <div class="chatbot-option-name">{chatbot.name}</div>
                                <div class="chatbot-option-provider">
                                    {chatbot.ai_provider?.name || 'Unknown Provider'}
                                </div>
                            </div>
                        </button>
                    {/each}
                </div>
            {/if}

            <div class="modal-actions">
                <button class="btn-cancel" on:click={() => showChatbotSelector = false}>
                    Cancelar
                </button>
            </div>
        </div>
    </div>
{/if}

<style>
    .conversation-detail {
        flex: 1;
        display: flex;
        flex-direction: column;
        background: var(--surface-1);
        height: 100%;
    }

    .detail-header {
        background: var(--surface-0);
        padding: 1.25rem 1.5rem;
        border-bottom: 1px solid var(--border-1);
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .contact-info {
        display: flex;
        align-items: center;
        gap: 1rem;
        min-width: 0;
    }

    /* Back button — only on mobile (full-screen chat → list) */
    .back-btn {
        display: none;
        width: 36px; height: 36px;
        align-items: center; justify-content: center;
        border: none; background: none; cursor: pointer;
        border-radius: 8px; color: var(--text-2);
        flex-shrink: 0; margin-left: -6px;
    }
    .back-btn:hover { background: var(--surface-1); color: var(--text-1); }

    .contact-avatar {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: var(--accent);
        color: var(--text-inverse);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        font-size: 1.25rem;
    }

    .contact-info h3 {
        margin: 0 0 0.25rem 0;
        font-size: 1.125rem;
        color: var(--text-1);
    }

    .contact-email {
        margin: 0;
        font-size: 0.875rem;
        color: var(--text-2);
    }

    .status-select {
        padding: 0.5rem 1rem;
        border: 1px solid var(--border-1);
        border-radius: 6px;
        background: var(--surface-0);
        cursor: pointer;
        font-size: 0.875rem;
    }

    /* Labels (etiquetas) */
    .labels-row {
        display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
        padding: 8px 16px;
        border-bottom: 1px solid var(--border-1);
        background: var(--surface-0);
    }
    .label-chip {
        display: inline-flex; align-items: center; gap: 4px;
        padding: 2px 8px;
        border-radius: 9999px;
        border: 1px solid transparent;
        font-size: 12px; font-weight: 600;
    }
    .label-x {
        border: none; background: none; cursor: pointer;
        font-size: 14px; line-height: 1; color: inherit; opacity: 0.7;
    }
    .label-x:hover { opacity: 1; }
    .label-add-wrap { position: relative; }
    .label-add {
        padding: 2px 10px; border-radius: 9999px;
        border: 1px dashed var(--border-2);
        background: none; cursor: pointer;
        font-size: 12px; color: var(--text-3);
    }
    .label-add:hover { color: var(--text-1); border-color: var(--text-3); }
    .label-picker {
        position: absolute; top: calc(100% + 4px); left: 0;
        min-width: 180px; max-height: 240px; overflow-y: auto;
        background: var(--surface-0);
        border: 1px solid var(--border-1);
        border-radius: 8px; box-shadow: var(--shadow-lg);
        padding: 4px; z-index: 30;
    }
    .label-picker-item {
        display: flex; align-items: center; gap: 8px;
        width: 100%; padding: 6px 10px;
        border: none; background: none; cursor: pointer;
        font-size: 13px; color: var(--text-1); border-radius: 6px; text-align: left;
    }
    .label-picker-item:hover { background: var(--surface-1); }
    .label-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
    .label-picker-empty { padding: 10px; font-size: 12px; color: var(--text-3); }

    /* Internal notes (handoff context, activity) — not customer messages */
    .internal-note {
        align-self: center;
        max-width: 80%;
        margin: 6px auto;
        padding: 8px 12px;
        background: var(--warning-soft, #fff7e6);
        border: 1px dashed var(--warning, #f0b429);
        border-radius: 8px;
        font-size: 12.5px;
        color: var(--text-2);
        text-align: center;
    }
    .internal-note .note-tag { font-weight: 700; color: #b45309; font-size: 11px; }
    .internal-note .note-body { margin: 3px 0; white-space: pre-line; }
    .internal-note .note-time { font-size: 10px; color: var(--text-3); }

    .messages-container {
        flex: 1;
        overflow-y: auto;
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .empty-messages {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: var(--text-3);
    }

    .message {
        display: flex;
    }

    .message-outgoing {
        justify-content: flex-end;
    }

    .message-incoming {
        justify-content: flex-start;
    }

    .message-bubble {
        max-width: 70%;
        padding: 10px 14px;
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-xs);
    }

    .message-outgoing .message-bubble {
        background: var(--accent);
        color: var(--text-inverse);
        border-bottom-right-radius: var(--radius-sm);
    }

    .message-incoming .message-bubble {
        background: var(--surface-0);
        border: 1px solid var(--border-1);
        color: var(--text-1);
        border-bottom-left-radius: var(--radius-sm);
    }

    .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;
        gap: 1rem;
    }

    .message-sender {
        font-weight: 600;
        font-size: 0.875rem;
    }

    .message-time {
        font-size: 0.75rem;
        opacity: 0.7;
    }

    .message-content {
        font-size: 0.9375rem;
        line-height: 1.5;
        word-wrap: break-word;
    }

    .message-input-container {
        background: var(--surface-0);
        padding: 1.25rem 1.5rem;
        border-top: 1px solid var(--border-1);
    }

    .message-form {
        display: flex;
        gap: 1rem;
    }

    .message-input {
        flex: 1;
        padding: 0.75rem 1rem;
        border: 1px solid var(--border-1);
        border-radius: 24px;
        font-size: 0.9375rem;
        outline: none;
    }

    .message-input:focus {
        border-color: var(--accent);
    }

    .send-button {
        padding: 0.75rem 1.5rem;
        background: var(--accent);
        color: var(--text-inverse);
        border: none;
        border-radius: 24px;
        cursor: pointer;
        font-size: 0.9375rem;
        font-weight: 600;
        transition: transform 0.2s;
    }

    .send-button:hover:not(:disabled) {
        transform: translateY(-1px);
    }

    .send-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .no-conversation {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: var(--text-3);
        font-size: 1.125rem;
    }

    /* AI Toggle */
    .conversation-actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .ai-toggle-button {
        padding: 0.5rem 1rem;
        border: 2px solid var(--border-1);
        border-radius: 6px;
        background: var(--surface-0);
        cursor: pointer;
        font-size: 0.875rem;
        font-weight: 600;
        transition: all 0.2s;
        color: var(--text-2);
    }

    .ai-toggle-button:hover:not(:disabled) {
        border-color: var(--accent);
        background: var(--accent-soft);
        color: var(--accent);
    }

    .ai-toggle-button.ai-active {
        background: var(--success);
        color: var(--text-inverse);
        border-color: var(--success);
    }

    .ai-toggle-button.ai-active:hover:not(:disabled) {
        background: var(--success);
        filter: brightness(0.9);
    }

    .ai-toggle-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .ai-status {
        margin: 0;
        font-size: 0.75rem;
        color: var(--success);
        font-weight: 500;
    }

    /* Modal */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .modal-content {
        background: var(--surface-0);
        border-radius: 12px;
        padding: 30px;
        width: 90%;
        max-width: 500px;
        max-height: 80vh;
        overflow-y: auto;
    }

    .modal-content h3 {
        margin: 0 0 10px 0;
        color: var(--text-1);
        font-size: 1.5rem;
    }

    .modal-description {
        margin: 0 0 20px 0;
        color: var(--text-2);
        font-size: 0.9rem;
    }

    .chatbot-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 20px;
    }

    .chatbot-option {
        display: flex;
        align-items: center;
        gap: 15px;
        padding: 15px;
        border: 2px solid var(--border-1);
        border-radius: 8px;
        background: var(--surface-0);
        cursor: pointer;
        transition: all 0.2s;
        text-align: left;
    }

    .chatbot-option:hover {
        border-color: var(--accent);
        background: var(--accent-soft);
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    }

    .chatbot-option-icon {
        font-size: 32px;
    }

    .chatbot-option-details {
        flex: 1;
    }

    .chatbot-option-name {
        font-weight: 600;
        color: var(--text-1);
        font-size: 1rem;
        margin-bottom: 4px;
    }

    .chatbot-option-provider {
        font-size: 0.85rem;
        color: var(--text-3);
    }

    .no-chatbots {
        text-align: center;
        padding: 40px 20px;
        color: var(--text-3);
    }

    .no-chatbots p {
        margin: 0 0 10px 0;
    }

    .no-chatbots .hint {
        font-size: 0.85rem;
        color: var(--text-3);
    }

    .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    .btn-cancel {
        padding: 0.75rem 1.5rem;
        border: 1px solid var(--border-1);
        border-radius: 6px;
        background: var(--surface-0);
        color: var(--text-2);
        cursor: pointer;
        font-size: 0.9rem;
        font-weight: 600;
        transition: all 0.2s;
    }

    .btn-cancel:hover {
        background: var(--surface-1);
    }

    /* Attachments */
    .message-attachments {
        margin-bottom: 8px;
    }

    .attachment-image img {
        max-width: 250px;
        max-height: 200px;
        border-radius: 8px;
        cursor: pointer;
    }

    .attachment-video {
        max-width: 300px;
        border-radius: 8px;
    }

    .attachment-audio {
        width: 100%;
        max-width: 300px;
    }

    .attachment-file {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: rgba(0,0,0,0.05);
        border-radius: 8px;
        text-decoration: none;
        color: inherit;
    }

    .file-icon {
        font-size: 20px;
    }

    /* File preview */
    .file-preview {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 12px;
        background: var(--surface-2);
        border-radius: 8px 8px 0 0;
        border-bottom: 1px solid var(--border-2);
    }

    .file-info {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .preview-thumb {
        width: 40px;
        height: 40px;
        object-fit: cover;
        border-radius: 4px;
    }

    .file-icon-large {
        font-size: 28px;
    }

    .file-name-preview {
        font-size: 13px;
        color: var(--text-1);
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .remove-file {
        background: none;
        border: none;
        font-size: 18px;
        cursor: pointer;
        color: var(--text-3);
        padding: 4px 8px;
    }

    .remove-file:hover {
        color: var(--danger);
    }

    .attach-button {
        background: none;
        border: none;
        font-size: 20px;
        cursor: pointer;
        padding: 8px;
        opacity: 0.7;
        transition: opacity 0.2s;
    }

    .attach-button:hover {
        opacity: 1;
    }

    .attach-button:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }
    /* Conversation labels */
    .conv-labels { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 6px; }
    .conv-label-chip { display: inline-flex; align-items: center; gap: 4px; color: #fff; font-size: 0.72rem;
                       font-weight: 600; padding: 2px 8px; border-radius: 999px; }
    .clc-x { background: rgba(255,255,255,.35); border: none; color: #fff; border-radius: 50%; width: 14px; height: 14px;
             line-height: 12px; cursor: pointer; font-size: 0.7rem; padding: 0; }
    .label-add-wrap { position: relative; }
    .label-add { font-size: 0.72rem; color: var(--text-2); background: transparent; border: 1px dashed var(--border-2);
                 border-radius: 999px; padding: 2px 8px; cursor: pointer; }
    .label-add:hover { color: var(--text-1); border-color: var(--accent); }
    .label-menu { position: absolute; top: 100%; left: 0; margin-top: 4px; background: var(--surface-0);
                  border: 1px solid var(--border-1); border-radius: 8px; padding: 4px; z-index: 20; min-width: 150px;
                  box-shadow: 0 4px 16px rgba(0,0,0,.15); display: flex; flex-direction: column; gap: 2px; }
    .label-opt { display: flex; align-items: center; gap: 8px; background: transparent; border: none; cursor: pointer;
                 padding: 6px 8px; border-radius: 6px; font-size: 0.8rem; color: var(--text-1); text-align: left; }
    .label-opt:hover { background: var(--surface-1); }
    .lo-dot { width: 10px; height: 10px; border-radius: 50%; }
    .label-none { font-size: 0.75rem; color: var(--text-3); padding: 6px 8px; }

    /* Lead / CRM drawer */
    .lead-toggle-button { display: inline-flex; align-items: center; gap: 4px; padding: 6px 10px;
                          border: 1px solid var(--border-2); background: transparent; border-radius: 8px;
                          font-size: 0.8rem; color: var(--text-2); cursor: pointer; }
    .lead-toggle-button:hover { color: var(--text-1); border-color: var(--accent); }
    .lead-toggle-button.has-lead { color: var(--text-1); border-color: var(--accent); }
    .lead-badge { font-size: 0.7rem; }
    .lead-badge.won { color: #16a34a; }
    .lead-badge.lost { color: #dc2626; }

    .lead-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.25); z-index: 40;
                     border: none; padding: 0; cursor: pointer; }
    .lead-drawer { position: fixed; top: 0; right: 0; height: 100%; width: 320px; max-width: 90vw;
                   background: var(--surface-0); border-left: 1px solid var(--border-1); z-index: 41;
                   box-shadow: -6px 0 24px rgba(0,0,0,.18); padding: 16px; display: flex;
                   flex-direction: column; gap: 14px; overflow-y: auto; }
    .lead-drawer-head { display: flex; align-items: center; justify-content: space-between; }
    .lead-drawer-head h4 { margin: 0; font-size: 1rem; }
    .lead-close { background: transparent; border: none; font-size: 1.4rem; line-height: 1; cursor: pointer;
                  color: var(--text-2); }
    .lead-muted { font-size: 0.85rem; color: var(--text-3); }
    .lead-create-btn { padding: 10px 12px; border: 1px dashed var(--border-2); background: transparent;
                       border-radius: 8px; cursor: pointer; color: var(--text-1); font-size: 0.85rem; }
    .lead-create-btn:hover:not(:disabled) { border-color: var(--accent); }
    .lead-card { display: flex; flex-direction: column; gap: 12px; }
    .lead-title { font-weight: 600; font-size: 0.95rem; }
    .lead-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .lead-value { font-size: 0.8rem; color: var(--text-2); }
    .lead-status { font-size: 0.72rem; font-weight: 600; padding: 2px 8px; border-radius: 999px; }
    .lead-status.active { background: rgba(148,163,184,.2); color: var(--text-2); }
    .lead-status.won { background: rgba(22,163,74,.15); color: #16a34a; }
    .lead-status.lost { background: rgba(220,38,38,.15); color: #dc2626; }
    .lead-field { display: flex; flex-direction: column; gap: 4px; font-size: 0.78rem; color: var(--text-2); }
    .lead-field select { padding: 8px; border: 1px solid var(--border-1); border-radius: 8px;
                         background: var(--surface-1); color: var(--text-1); }
    .lead-actions { display: flex; gap: 8px; }
    .lead-actions button { flex: 1; padding: 8px; border-radius: 8px; border: 1px solid var(--border-1);
                           cursor: pointer; font-size: 0.82rem; }
    .lead-won { background: rgba(22,163,74,.12); color: #16a34a; }
    .lead-lost { background: rgba(220,38,38,.12); color: #dc2626; }
    .lead-actions button:disabled { opacity: .5; cursor: not-allowed; }

    /* ===== Mobile: WhatsApp-style chat ===== */
    @media (max-width: 768px) {
        .back-btn { display: flex; }

        .detail-header {
            padding: 8px 10px;
            gap: 8px;
            position: sticky; top: 0; z-index: 5;
        }
        .contact-info { gap: 8px; flex: 1; }
        .contact-avatar { width: 38px; height: 38px; font-size: 1rem; }
        .contact-info h3 { font-size: 1rem; line-height: 1.2; }
        .contact-email { font-size: 0.75rem; }
        /* Keep header actions tight: AI toggle shrinks, status select compact */
        .conversation-actions { gap: 6px; flex-shrink: 0; }
        .ai-toggle-button { padding: 6px 8px; font-size: 0; gap: 0; }
        .ai-toggle-button::after { content: '🤖'; font-size: 16px; }
        .status-select { padding: 6px 8px; font-size: 13px; max-width: 108px; }

        .messages-container { padding: 12px 10px; gap: 8px; }
        .message-bubble { max-width: 82%; }

        /* Anchored composer: never need to scroll to reach it; respect the
           home-indicator safe area on iOS. */
        .message-input-container {
            padding: 8px 10px;
            padding-bottom: calc(8px + env(safe-area-inset-bottom));
        }
        .message-form { gap: 8px; }
        .message-input { padding: 0.6rem 0.9rem; }
        .send-button { padding: 0.6rem 1rem; }
    }
</style>
